import ProfileRow from '@/components/Profile/ProfileRow'
import { isValidPubkey } from '@/lib/pubkey'
import { pointerFromATag, tagNameEquals } from '@/lib/tag'
import { cn } from '@/lib/utils'
import type { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Username from '@/components/Username'
import {
  BadgeThumb,
  badgeDisplayName,
  parseBadgeDefinition,
  pickBadgeImage,
  useBadgeDefinition
} from './badge-utils'
import { SecondaryPageLink } from '@/PageManager'
import { toNote } from '@/lib/link'
import Image from '@/components/Image'

/** NIP-58 kind 8 badge award: issuer awards one badge to N recipients. */
export default function BadgeAward({
  event,
  className,
  hideDefinition
}: {
  event: Event
  className?: string
  hideDefinition?: boolean
}) {
  const { t } = useTranslation()
  const definitionPointer = useMemo(() => {
    const aTag = event.tags.find(tagNameEquals('a'))
    return aTag ? pointerFromATag(aTag) : undefined
  }, [event])
  const recipients = useMemo(
    () =>
      event.tags
        .filter((tag) => tag[0] === 'p' && tag[1] && isValidPubkey(tag[1]))
        .map((tag) => tag[1]),
    [event]
  )
  const { value: definitionEvent } = useBadgeDefinition(definitionPointer)
  const meta = definitionEvent ? parseBadgeDefinition(definitionEvent) : undefined
  const name = meta ? badgeDisplayName(meta) : undefined
  const image = meta ? pickBadgeImage(meta, true) : undefined

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-sm font-medium text-muted-foreground">{t('Badge award')}</div>
      {hideDefinition ? null : (
        <div className="flex gap-4">
          {image ? (
            <Image
              imageUrl={image}
              imagePubkey={definitionEvent?.pubkey ?? event.pubkey}
              alt={name}
              classNames={{ wrapper: 'aspect-square w-24 shrink-0 rounded-xl border bg-muted' }}
            />
          ) : (
            <BadgeThumb meta={meta} pubkey={definitionPointer?.pubkey} size="lg" />
          )}
          <div className="min-w-0 flex-1 space-y-1">
            {definitionPointer && (
              <SecondaryPageLink
                to={toNote(definitionEvent ?? definitionPointer)}
                onClick={(e) => e.stopPropagation()}
                className="block truncate text-xl font-semibold hover:underline"
              >
                {name ?? (
                  <span className="font-mono text-sm text-muted-foreground">
                    {definitionPointer.identifier || t('Unknown badge')}
                  </span>
                )}
              </SecondaryPageLink>
            )}
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <span>{t('Issued by')}</span>
              <Username user={event.pubkey} className="font-semibold" />
            </div>
            <div className="text-sm text-muted-foreground">
              {t('Awarded to {{count}} {{count, plural, one {user} other {users}}}', {
                count: recipients.length
              })}
            </div>
          </div>
        </div>
      )}
      {recipients.length > 0 && (
        <div className="space-y-1">
          {recipients.map((pubkey) => (
            <ProfileRow key={pubkey} pubkey={pubkey} />
          ))}
        </div>
      )}
    </div>
  )
}
